import { List } from "immutable";
import createCachedSelector from "re-reselect";
import { createSelector } from "reselect";

import { SPREADSHEET_VIEW } from "../../constants/identifiers";
import outlineColumnIds from "../../constants/outlineColumnIds";
import tableRowTypes from "../../constants/tableRowTypes";
import inspectorSelectors from "../../features/inspector/selectors";
import { appStateSelectors } from "../../modules/appState";
import { definitionSelectors } from "../../modules/definition";
import { calendarSelectors } from "../../modules/definition/calendar";
import { propertySelectors } from "../../modules/definition/properties";
import { typeSelectors } from "../../modules/definition/types";
import { itemSelectors } from "../../modules/items";
import sortFunctions, { getReverseSortFunction } from "../../modules/items/sortFunctions";
import superAndChildrenSelectors from "../../modules/superAndChildrenById/selectors";
import { tagSelectors } from "../../modules/tags";
import { uiStateSelectors } from "../../modules/uiState";
import itemCardSelectors from "../itemCard/selectors";
import { findCardWithId } from "../narrativeView/utils";
import tableViewSelectors from "../sharedSelectors/tableViewSelectors";
import workViewSelectors from "../workView/selectors";

const getRowsWithSuperId = (items, superId, expandedItemsSet, sortFunction, superAndChildrenById, sortInDesc) => {
  // get all items with the requested super
  let filterFunc = null;
  if (superId) {
    const superChildren = superAndChildrenById.get(superId).children.toSet();
    filterFunc = o => superChildren.has(o.id);
  } else {
    filterFunc = o => !o.super;
  }

  const thisLevel = items.filter(filterFunc).sort(sortInDesc ? getReverseSortFunction(sortFunction) : sortFunction);
  // Now work out if we need to get any nested children
  let thisLevelAndChildren = thisLevel;

  let insertedRows = 0;

  // Iterate through each item at this level...
  thisLevel.forEach((o, index) => {
    if (o.children && expandedItemsSet.has(o.id)) {
      // This item is expanded with children, so make a recursive call to grab their children also
      const children = getRowsWithSuperId(items, o.id, expandedItemsSet, sortFunction, superAndChildrenById);
      // And now we want to insert them after this item.
      // const insertionIndex = allLevels.findIndex(a => a.id === o.id);
      const insertionIndex = index + insertedRows;
      thisLevelAndChildren = thisLevelAndChildren.insert(insertionIndex + 1, children).flatten();
      insertedRows += children.size;
    }
  });
  // Return the items at this level
  return thisLevelAndChildren;
};

const getRowsWithItems = createCachedSelector(
  // fetch parameters for last calculate function
  (_state, _viewId, items) => items,
  appStateSelectors.getViewExpandedItems,
  typeSelectors.getSequenceTypeIds,
  appStateSelectors.getSpreadsheetViewSortBy,
  calendarSelectors.getCalendar,
  typeSelectors.getTypesById,
  propertySelectors.getPropertiesById,
  superAndChildrenSelectors.getSuperAndChildrenById,
  appStateSelectors.getSpreadsheetViewSortInDesc,
  // Calculate result
  (items, expandedItems, types, sortBy, calendar, typesById, propertiesById, superAndChildrenById, sortInDesc) => {
    // Get all items that match the desired type
    const typeSet = types.toSet();
    let filtered = items.filter(o => typeSet.has(o.type));

    // Filter out the children of items
    filtered = filtered.map(item =>
      item.update("children", children => children.filter(cId => filtered.find(i => i.id === cId)))
    );

    // Use recursive approach starting at top level
    return getRowsWithSuperId(
      filtered,
      null,
      expandedItems.toSet(),
      sortFunctions.sortFunctionForColumn(sortBy, calendar, typesById, propertiesById),
      superAndChildrenById,
      sortInDesc
    );
  }
)(workViewSelectors.getViewId);

const getRowsWithItemsForMobile = createCachedSelector(
  // fetch parameters for last calculate function
  (_state, _viewId, items) => items,
  appStateSelectors.getViewExpandedItemsForMobile,
  typeSelectors.getSequenceTypeIds,
  calendarSelectors.getCalendar,
  typeSelectors.getTypesById,
  propertySelectors.getPropertiesById,
  superAndChildrenSelectors.getSuperAndChildrenById,
  // Calculate result
  (items, expandedItems, types, calendar, typesById, propertiesById, superAndChildrenById) => {
    // Get all items that match the desired type
    const filtered = items.filter(o => types.contains(o.type));
    // Use recursive approach starting at top level
    return getRowsWithSuperId(
      filtered,
      null,
      expandedItems.toSet(),
      sortFunctions.sortFunctionForColumn("sequence", calendar, typesById, propertiesById),
      superAndChildrenById
    );
  }
)(workViewSelectors.getViewId);

// Sort Events - Makes sure selected events are in correct order...
/**
 *
 * @param {TimelineRecord} state
 * @param {string} viewId
 * @param {List} items
 */
const getRows = createCachedSelector(
  state => state,
  (_state, viewId) => viewId,
  workViewSelectors.getItemsInGroupForView,
  getRowsWithItems
)(workViewSelectors.getViewId);

/**
 * @param {TimelineRecord} state
 * @param {string} viewId
 */
const getRowsForMobile = createCachedSelector(
  state => state,
  (_state, viewId) => viewId,
  workViewSelectors.getItemsInGroupForViewForMobile,
  getRowsWithItemsForMobile
)(workViewSelectors.getViewId);

const getSelectedRows = createCachedSelector(
  state => state,
  (_state, viewId) => viewId,
  workViewSelectors.getSelectedItemsInGroupForView,
  getRowsWithItems
)(workViewSelectors.getViewId);

const getSpreadsheetViewAllowedColumns = (state, viewId) =>
  tableViewSelectors.getAllowedColumns(state, viewId, typeSelectors.getOrderedSequenceTypes(state), true);
const getSpreadsheetViewAvailableColumns = (state, viewId) =>
  tableViewSelectors.getAvailableColumns(state, viewId, typeSelectors.getOrderedSequenceTypes(state), true);

const getSpreadsheetViewOptionsForColumns = (state, rowId, columnId) => {
  if (rowId !== tableRowTypes.EMPTY && (columnId == "blocks" || columnId == "blockedBy")) {
    return inspectorSelectors.getDependencyOptions(state, List([rowId]));
  }
  return tableViewSelectors.getOptionsForColumn(state, typeSelectors.getOrderedSequenceTypes(state), columnId, rowId);
};

/**
 *
 * @param {TimelineRecord} state
 * @param {string} viewId
 */
const getSpreadsheetViewColumns = createCachedSelector(
  getSpreadsheetViewAllowedColumns,
  appStateSelectors.getSpreadsheetOptionsForView,
  appStateSelectors.getFontSize,
  // calculate result: (list of entities matching the type)
  tableViewSelectors.getColumns
)(workViewSelectors.getViewId);

/**
 *
 * @param {TimelineRecord} state
 * @param {string} viewId
 */
const getSpreadsheetViewData = (state, viewId) => {
  const rows = getRows(state, viewId);
  const columns = getSpreadsheetViewColumns(state, viewId);
  return tableViewSelectors.getTableData(state, viewId, rows, columns, "nestedTitleMetrics", false, false);
};

const getSpreadsheetViewSelectedData = (state, viewId) =>
  tableViewSelectors.getTableData(
    state,
    viewId,
    getSelectedRows(state, viewId),
    getSpreadsheetViewColumns(state, viewId),
    "nestedTitleMetrics",
    false,
    false
  );

/**
 *
 * @param {TimelineRecord} state
 * @param {string} viewId
 */
const getSpreadsheetViewSortedItemIds = (state, viewId) => {
  return tableViewSelectors.getSortedRowIds(state, viewId, getRows(state, viewId));
};

const getSpreadsheetViewSortedItemIdsForMobile = (state, viewId, viewWidth, showDragHandles) => {
  return tableViewSelectors.getSortedRowIds(state, viewId, getRowsForMobile(state, viewId, viewWidth, showDragHandles));
};

const getSpreadsheetViewDefaultType = state => definitionSelectors.getDefaultSequenceType(state);

// const getSpreadsheetViewDataForItem = (displayData, itemId) => {
//   // See if there is already a row for this item.
//   const row = displayData.rows.find(r => r.id === itemId);
//   return row;
// };

const getSpreadsheetViewDataForItem = createSelector(
  // fetch parameters for last calculate function
  (_state, viewId, _displayData, _itemId, _itemsById, _typesById) => viewId,
  (_state, _viewId, displayData, _itemId, _itemsById, _typesById) => displayData,
  (_state, _viewId, _displayData, itemId, _itemsById, _typesById) => itemId,
  (state, viewId, _displayData, _itemId, _itemsById, _typesById) =>
    workViewSelectors.getDisplayItemsForViewType(state, SPREADSHEET_VIEW, viewId),
  getSpreadsheetViewColumns,
  (_state, _viewId, _displayData, _itemId, itemsById, _typesById) => itemsById,
  (_state, _viewId, _displayData, _itemId, _itemsById, typesById) => typesById,
  state => definitionSelectors.getEnabledDefinitions(state).colors.byId,
  state => definitionSelectors.getReferencesById(state),
  state => definitionSelectors.getPropertiesById(state),
  state => state.data.relationships.byId,
  state => state.data.dependencies.byId,
  appStateSelectors.getViewExpandedItems,
  state => itemSelectors.getItemIndexesInTreeOrderForAllTypes(state),
  tagSelectors.getTags,
  // and calculate
  (
    viewId,
    displayData,
    itemId,
    displayItems,
    columns,
    itemsById,
    typesById,
    colorsById,
    referencesById,
    propertiesById,
    relationshipsById,
    dependenciesById,
    expandedItems,
    globalItemIndexes,
    tagsById
  ) => {
    // See if there is already a row for this item.
    const row = displayData.rows ? findCardWithId(displayData.rows, itemId) : null;
    if (row) {
      return row;
    }
    // Otherwise, create a row
    const displayItem = displayItems.find(obj => obj.id === itemId);
    const rowRecord = tableViewSelectors.getIndividualTableRow(
      viewId,
      displayItem,
      0,
      columns,
      itemsById,
      typesById,
      colorsById,
      referencesById,
      propertiesById,
      relationshipsById,
      dependenciesById,
      expandedItems,
      "nestedTitleMetrics",
      false,
      null,
      null,
      null,
      false,
      globalItemIndexes,
      null,
      null,
      tagsById
    );
    return rowRecord;
  }
);

const getSortableColumnIds = createSelector(
  // input
  propertySelectors.getPropertiesById,
  // output
  propertiesById => {
    const defaultColumns = [
      outlineColumnIds.label,
      outlineColumnIds.identifier,
      outlineColumnIds.start,
      outlineColumnIds.sequence,
      outlineColumnIds.duration,
      outlineColumnIds.end,
      outlineColumnIds.type,
      outlineColumnIds.color
    ];
    return propertiesById
      .map(p => p.id)
      .toList()
      .push(List(defaultColumns))
      .flatten();
  }
);

// ==========================
// Mobile Item Card Selectors
// ==========================

/**
 * @param {TimelineRecord} state
 * @param {string} viewId
 * @param {number} maxWidth
 * @param {boolean} showDragHandles
 */
const getItemCardListRows = createCachedSelector(
  // fetch parameters for last calculate function
  workViewSelectors.getItemsInGroupForViewForMobile,
  appStateSelectors.getViewExpandedItemsForMobile,
  typeSelectors.getSequenceTypeIds,
  calendarSelectors.getCalendar,
  typeSelectors.getTypesById,
  propertySelectors.getPropertiesById,
  superAndChildrenSelectors.getSuperAndChildrenById,
  // Calculate result
  (items, expandedItems, sequenceTypes, calendar, typesById, propertiesById, superAndChildrenById) => {
    // Get all objects that match the desired types
    const filtered = items.filter(o => sequenceTypes.contains(o.type));
    return getRowsWithSuperId(
      filtered,
      null,
      expandedItems.toSet(),
      sortFunctions.sortFunctionForColumn("sequence", calendar, typesById, propertiesById),
      superAndChildrenById
    );
  }
)(workViewSelectors.getViewId);

/**
 * @param {TimelineRecord} state
 * @param {string} viewId
 * @param {number} maxWidth
 */
const getItemCardTableData = createCachedSelector(
  // fetch parameters for last calculate function
  state => state.data.items.byId,
  getItemCardListRows,
  appStateSelectors.getViewExpandedItemsForMobile,
  () => "itemCardNestedTitleMetrics",
  // Calculate
  itemCardSelectors.getTableData
)(workViewSelectors.getViewId);

/**
 * @param {TimelineRecord} state
 * @param {string} viewId
 * @param {number} maxWidth
 * @param {boolean} showDragHandles
 */
const getItemCardListViewData = createCachedSelector(
  getItemCardTableData,
  uiStateSelectors.getSelectedItems,
  (data, selection) => {
    return data.update("rows", rows =>
      rows.map(r => r.setIn(["itemDisplay", "selected"], selection.includes(r.itemDisplay.id)))
    );
  }
)(workViewSelectors.getViewId);

export default {
  getSpreadsheetViewAvailableColumns,
  getSpreadsheetViewData,
  getSpreadsheetViewOptionsForColumns,
  getSpreadsheetViewSelectedData,
  getSpreadsheetViewSortedItemIds,
  getSpreadsheetViewSortedItemIdsForMobile,
  getSpreadsheetViewDefaultType,
  getItemCardListViewData,
  getSpreadsheetViewDataForItem,
  getSortableColumnIds
};
